import { prisma } from "@marble/db/client";
import {
  AppointmentType,
  CareSponsorType,
  LineOfBusiness,
  State,
  UserType,
} from "@prisma/client";

import {
  ALLOTMENT_EXEMPT_UNEXCUSED_NO_SHOW_COUNT,
  GUARDIAN_CONSULT_SPONSORSHIP_CAP,
  LATE_CANCELLATION_WINDOW_HOURS,
  ONE_HOUR_MS,
} from "@marble/core/modules/care-sponsorship/constants";
import {
  getCareSponsorshipCapGatedAppointmentTypes,
  isCareSponsorshipCapGatedAppointmentType,
  isCareSponsorshipEligibleAppointmentTypeForSponsorType,
  isIntakeCapGatedForCareSponsorship,
  isMarbleCareSponsorship,
} from "@marble/core/modules/care-sponsorship/utils";
import { getIsPayerLobINN } from "@marble/core/modules/insurance/utils";

import {
  CanBookAdditionalSponsoredSessionResult,
  CareSponsorshipSessionAppointment,
  CareSponsorshipSessionUsage,
  ResolveCareSponsorshipIdForBookingResult,
  SponsoredSessionCapReachedEnding,
  UpcomingSponsoredSessionEnding,
} from "@marble/core/modules/care-sponsorship/types";

/**
 * Resolves the active CareSponsor for a school — used at referral time
 * (referrer's school) and at manual enrollment (student's school).
 *
 * Branches on CareSponsorType: SCHOOL matches schoolId directly;
 * SCHOOL_NETWORK matches the school's schoolNetworkId (e.g. Success Academy).
 */
export const getActiveCareSponsorForSchool = async (schoolId: number) => {
  const school = await prisma.school.findUnique({
    where: { id: schoolId },
    select: { schoolNetworkId: true },
  });
  if (!school) {
    return null;
  }

  return prisma.careSponsor.findFirst({
    where: {
      active: true,
      OR: [
        { careSponsorType: CareSponsorType.SCHOOL, schoolId },
        ...(school.schoolNetworkId != null
          ? [
              {
                careSponsorType: CareSponsorType.SCHOOL_NETWORK,
                schoolNetworkId: school.schoolNetworkId,
              },
            ]
          : []),
      ],
    },
  });
};

/**
 * Resolves the active CareSponsor for a client's insurance — matches on the
 * same compound key as `InsurancePayerNetworkStatus`'s own unique index
 * (payer + line of business + state). `CareSponsor.insurancePayerNetworkStatus`
 * is itself `@unique`, so at most one CareSponsor can ever back a given
 * payer/LOB/state — no ambiguity to resolve, unlike the school matcher above.
 *
 * Returns `null` if that network status is explicitly OON, using the same
 * {@link getIsPayerLobINN} check (with `considerNotAcceptingNewClientsOON:
 * true`) that `getIsInsuranceInfoOON` and every other INN/OON determination
 * in the codebase uses — `isINN: false` (a real out-of-network
 * determination) or `acceptingNewClients: false` (treated as OON
 * everywhere else). A `sponsoringCare` payer is expected to have `isINN:
 * true` by convention (that's the whole point of `sponsoringCare`), so this
 * mainly guards against a network status that's since flipped OON without
 * its `CareSponsor` being deactivated, and against auto-enrolling new
 * clients into a payer relationship that's mid-wind-down
 * (`acceptingNewClients: false`).
 */
export const getActiveCareSponsorForInsurancePayer = async ({
  insurancePayerId,
  lineOfBusiness,
  state,
}: {
  insurancePayerId: number;
  lineOfBusiness: LineOfBusiness;
  state: State;
}) => {
  const networkStatus = await prisma.insurancePayerNetworkStatus.findUnique({
    where: {
      insurancePayerId_lineOfBusiness_state: {
        insurancePayerId,
        lineOfBusiness,
        state,
      },
    },
    select: { isINN: true, acceptingNewClients: true, CareSponsor: true },
  });

  if (!networkStatus) {
    return null;
  }

  const isINN = getIsPayerLobINN({
    state,
    lineOfBusiness,
    payerNetworkStatuses: [
      {
        state,
        lineOfBusiness,
        isINN: networkStatus.isINN,
        acceptingNewClients: networkStatus.acceptingNewClients,
      },
    ],
    considerNotAcceptingNewClientsOON: true,
  });
  if (!isINN) {
    return null;
  }

  const careSponsor = networkStatus.CareSponsor;
  return careSponsor?.active ? careSponsor : null;
};

/**
 * Resolves the active MARBLE_STATE_UNINSURED_PATIENTS CareSponsor for a
 * state — Marble's own program for clients it cannot bill because they have
 * no insurance at all (as opposed to
 * {@link getActiveCareSponsorForInsurancePayer}, which resolves a real
 * payer relationship). Returns `null` when the program is not live in that
 * state, i.e. no CareSponsor row exists for it or the rollout switch
 * (`active`) is off.
 *
 * `CareSponsor.state` is `@unique`, so at most one CareSponsor can ever
 * cover a state — no ambiguity to resolve. The `careSponsorType` filter is
 * still explicit rather than inferred from `state` being set, matching how
 * every other resolver here keys off the discriminator.
 *
 * Eligibility is the state alone: unlike the school trigger, there's no
 * referral-source or cutoff gate — a client with no insurance in a covered
 * state qualifies however they reached Marble.
 */
export const getActiveMarbleStateUninsuredCareSponsor = async (
  state: State,
) => {
  return prisma.careSponsor.findFirst({
    where: {
      active: true,
      careSponsorType: CareSponsorType.MARBLE_STATE_UNINSURED_PATIENTS,
      state,
    },
  });
};

/**
 * All CareSponsors for the admin Care Sponsors table, with the
 * School/SchoolNetwork/InsurancePayerNetworkStatus relation each one
 * resolves eligibility against. Ordered by name for a stable table listing.
 */
export const getAllCareSponsors = async () => {
  return prisma.careSponsor.findMany({
    include: {
      School: { select: { id: true, name: true } },
      SchoolNetwork: { select: { id: true, name: true } },
      InsurancePayerNetworkStatus: {
        select: {
          id: true,
          insurancePayerContractId: true,
          lineOfBusiness: true,
          state: true,
          InsurancePayer: { select: { id: true, name: true } },
        },
      },
    },
    orderBy: { name: "asc" },
  });
};

/**
 * InsurancePayerNetworkStatus rows that are actually candidates for a new
 * CareSponsor — the selectable options for the admin "Add Care Sponsor"
 * (Insurance Payer type) form. Requires `sponsoringCare: true` (Marble is
 * treating this payer/line-of-business/state as INN specifically to sponsor
 * care ahead of a real contract — see the field's schema doc comment; that's
 * the whole reason this CareSponsor type exists) and excludes statuses
 * already tied to a CareSponsor, since resolving eligibility against two
 * CareSponsors for the same payer/line of business/state would be ambiguous.
 */
export const getSelectableInsurancePayerNetworkStatusesForCareSponsor =
  async () => {
    return prisma.insurancePayerNetworkStatus.findMany({
      where: { sponsoringCare: true, CareSponsor: { is: null } },
      select: {
        id: true,
        lineOfBusiness: true,
        state: true,
        insurancePayerContractId: true,
        InsurancePayer: { select: { id: true, name: true } },
      },
      orderBy: [{ InsurancePayer: { name: "asc" } }, { state: "asc" }],
    });
  };

/**
 * States a new MARBLE_STATE_UNINSURED_PATIENTS CareSponsor can be created for
 * — the selectable options for the admin "Add Care Sponsor" (State Uninsured
 * Program) form. Candidates are the states Marble operates in at all, i.e.
 * those with a StateConfiguration row (including not-yet-active ones, so a
 * sponsor can be set up ahead of a state going live).
 *
 * States already backing a sponsor of this type are excluded:
 * `CareSponsor.state` is unique, so a second one would fail on the DB
 * constraint, and two sponsors for one state would be ambiguous to resolve
 * eligibility against anyway (same reason the insurance-payer selectable
 * query above excludes statuses already tied to a CareSponsor).
 */
export const getSelectableStatesForUninsuredCareSponsor = async (): Promise<
  State[]
> => {
  const [configuredStates, existingSponsors] = await Promise.all([
    prisma.stateConfiguration.findMany({
      select: { state: true },
      orderBy: { state: "asc" },
    }),
    prisma.careSponsor.findMany({
      where: {
        careSponsorType: CareSponsorType.MARBLE_STATE_UNINSURED_PATIENTS,
      },
      select: { state: true },
    }),
  ]);

  const takenStates = new Set(
    existingSponsors
      .map((sponsor) => sponsor.state)
      .filter((state): state is State => state !== null),
  );

  return configuredStates
    .map((config) => config.state)
    .filter((state) => !takenStates.has(state));
};

/**
 * The student's currently-active CareSponsorship, if any — read via
 * `Student.activeCareSponsorshipId`. Mirrors the existing
 * `Student.activeInsuranceInfoId` lookup pattern.
 */
export const getActiveCareSponsorshipForStudent = async (studentId: number) => {
  const student = await prisma.student.findUnique({
    where: { id: studentId },
    select: {
      ActiveCareSponsorship: {
        include: { CareSponsor: true },
      },
    },
  });

  const sponsorship = student?.ActiveCareSponsorship ?? null;
  if (!sponsorship || sponsorship.endedAt != null) {
    return null;
  }
  return sponsorship;
};

export const hasActiveCareSponsorship = async (studentId: number) => {
  const activeCareSponsorship =
    await getActiveCareSponsorshipForStudent(studentId);
  return !!activeCareSponsorship;
};

/**
 * Whether this student is currently under a Marble-covering care sponsorship
 * — insurance-payer or state-uninsured (see `isMarbleCareSponsorship`), NOT
 * a school sponsorship. `studentId == null` (e.g. read off a legacy
 * InsuranceInfo row with no student) is never sponsored.
 */
export const isStudentUnderMarbleCareSponsorship = async (
  studentId: number | null | undefined,
): Promise<boolean> => {
  if (!studentId) {
    return false;
  }
  const activeCareSponsorship =
    await getActiveCareSponsorshipForStudent(studentId);
  if (!activeCareSponsorship) {
    return false;
  }
  return isMarbleCareSponsorship(
    activeCareSponsorship.CareSponsor.careSponsorType,
  );
};

/**
 * The student's active CareSponsorship if — and only if — it is the
 * MARBLE_STATE_UNINSURED_PATIENTS program (see
 * {@link getActiveMarbleStateUninsuredCareSponsor}). `null` for a student
 * with no active sponsorship *or* one backed by any other sponsor type.
 *
 * For copy/logic that varies on "is Marble covering this, or a school",
 * prefer `isMarbleCareSponsorship` on the resolved sponsor type — this is
 * for the narrower "is this specifically the state-uninsured program"
 * question, where an insurance-payer bridging sponsorship must not match.
 */
export const getActiveMarbleStateUninsuredCareSponsorship = async (
  studentId: number,
) => {
  const activeCareSponsorship =
    await getActiveCareSponsorshipForStudent(studentId);
  if (
    activeCareSponsorship?.CareSponsor.careSponsorType !==
    CareSponsorType.MARBLE_STATE_UNINSURED_PATIENTS
  ) {
    return null;
  }
  return activeCareSponsorship;
};

/**
 * Same lookup as {@link getActiveCareSponsorshipForStudent}, resolved from a
 * client's `User.id` instead of their `Student.id` — for callers (e.g.
 * therapist matching) that only have the client's user id on hand. Returns
 * `null` for non-students and for students without an active sponsorship.
 */
export const getActiveCareSponsorshipForClientUserId = async (
  clientUserId: number,
) => {
  const student = await prisma.student.findUnique({
    where: { userId: clientUserId },
    select: { id: true },
  });
  if (!student) return null;
  return getActiveCareSponsorshipForStudent(student.id);
};

/**
 * Shared prefix for both sponsorship-applicability checks below: the
 * appointment type must be sponsorship-eligible, the client must resolve to
 * a student, and that student must have an active sponsorship. Returns the
 * resolved studentId (so a cap check afterward doesn't re-look-up the
 * student) or `null` if any of those gates fail.
 */
const _resolveEligibleActiveSponsorshipStudentId = async (
  clientUserId: number,
  appointmentType: AppointmentType,
): Promise<{
  studentId: number;
  careSponsorType: CareSponsorType;
} | null> => {
  const student = await prisma.student.findUnique({
    where: { userId: clientUserId },
    select: { id: true },
  });
  if (!student) return null;

  const activeCareSponsorship = await getActiveCareSponsorshipForStudent(
    student.id,
  );
  if (!activeCareSponsorship) return null;

  const careSponsorType = activeCareSponsorship.CareSponsor.careSponsorType;
  if (
    !isCareSponsorshipEligibleAppointmentTypeForSponsorType(
      appointmentType,
      careSponsorType,
    )
  ) {
    return null;
  }

  return { studentId: student.id, careSponsorType };
};

/**
 * True when care sponsorship should drive scheduling/billing for this
 * appointment: active sponsorship, covered type, and cap/consent.
 * When there is an active sponsorship, we assume that insurance information is already verified for coverage/billability
 * for the present time.
 */
export const canCareSponsorshipApplyToAppointment = async (
  clientUserId: number,
  appointmentType: AppointmentType,
) => {
  const student = await _resolveEligibleActiveSponsorshipStudentId(
    clientUserId,
    appointmentType,
  );
  if (student == null) return false;

  return canBookAdditionalSponsoredSession(student.studentId, {
    appointmentType,
  });
};

/**
 * True when an ALREADY-BOOKED appointment remains covered by care
 * sponsorship: active sponsorship + eligible type. Unlike
 * `canCareSponsorshipApplyToAppointment`, this does NOT gate on session-cap
 * headroom — an existing appointment already consumed its cap slot when it
 * was booked, so re-running the "can an ADDITIONAL session still be booked"
 * check against it would misread a fully-consumed cap as "not sponsored"
 * even though the sponsor still covers these sessions.
 *
 * Use this to evaluate an already-scheduled appointment (e.g. re-checking
 * upcoming appointments after an insurance change); use
 * `canCareSponsorshipApplyToAppointment` when deciding whether a NEW session
 * can be booked.
 */
export const canCareSponsorshipCoverAlreadyBookedAppointment = async (
  clientUserId: number,
  appointmentType: AppointmentType,
) => {
  const studentId = await _resolveEligibleActiveSponsorshipStudentId(
    clientUserId,
    appointmentType,
  );
  return studentId != null;
};

/**
 * The crux function for session-cap tracking. Computed on read from
 * UserAppointment rows stamped with a CareSponsorship — not stored — so a
 * no-show correction self-corrects every later ordinal.
 *
 * By default uses the student's active sponsorship. Pass `careSponsorshipId`
 * to compute usage for a specific (e.g. ended) sponsorship belonging to the
 * student.
 *
 * Rules:
 *  - Which appointment types consume the family's session allotment depends
 *    on the sponsorship's `CareSponsorType` AND (for INTAKE specifically) its
 *    `startedAt` — see `getCareSponsorshipCapGatedAppointmentTypes`.
 *    INDIVIDUAL_THERAPY always counts; for the Marble sponsor types
 *    (INSURANCE_PAYER_NETWORK_STATUS, MARBLE_STATE_UNINSURED_PATIENTS) INTAKE
 *    additionally counts, but only for sponsorships started on/after
 *    `CARE_SPONSORSHIP_INTAKE_CAP_GATING_CUTOVER_DATE` — sponsorships that
 *    predate that cutover keep INTAKE free of the cap permanently, so this
 *    change doesn't retroactively recount an already-completed INTAKE for
 *    every pre-existing sponsorship the moment it ships. GUARDIAN_CONSULT is
 *    still covered/stamped for sponsor billing but is never in this count
 *    (and never gets an ordinal) — it has its own separate cap
 *    (`GUARDIAN_CONSULT_SPONSORSHIP_CAP`).
 *  - A therapist-initiated cancellation is excluded entirely (no ordinal,
 *    doesn't count) — this is never the family's or the sponsor's issue.
 *  - A client cancellation <24h before start counts the same as a no-show;
 *    >=24h doesn't count at all.
 *  - An excused no-show (noShowExcusedReason set, either reason) never
 *    counts
 *  - The first N unexcused no-show-like events are still billed at the normal
 *    rate (`billableToSponsor: true`) but excluded from the family's cap /
 *    allotment (`countsTowardCap: false`, no `ordinal`) — every one after
 *    that is also billable and counts toward the cap.
 *  - A still-upcoming (not yet occurred) appointment counts toward the cap
 *    (to prevent overbooking) but is reported separately as
 *    `scheduledSessions`, not `completedSessions`.
 */
export const getCareSponsorshipSessionUsage = async ({
  studentId,
  careSponsorshipId,
}: {
  studentId: number;
  careSponsorshipId?: number;
}): Promise<CareSponsorshipSessionUsage> => {
  const careSponsorship =
    careSponsorshipId != null
      ? await prisma.careSponsorship.findFirst({
          where: { id: careSponsorshipId, studentId },
          include: { CareSponsor: true },
        })
      : await getActiveCareSponsorshipForStudent(studentId);

  if (!careSponsorship) {
    return {
      completedSessions: 0,
      scheduledSessions: 0,
      usedSessions: 0,
      cap: null,
      appointments: [],
      careSponsorType: null,
      sponsorshipStartedAt: null,
      intakeCountsTowardCap: false,
    };
  }

  const cap =
    careSponsorship.sessionCap ??
    careSponsorship.CareSponsor.defaultSessionCap ??
    null;

  // Which appointment types draw from this sponsorship's numeric cap
  // depends on its CareSponsorType AND (for INTAKE) its startedAt — see
  // getCareSponsorshipCapGatedAppointmentTypes's doc comment
  // (INDIVIDUAL_THERAPY for every sponsor type; also MEDICATION_MANAGEMENT
  // and FAMILY_THERAPY for the Marble sponsor types; INTAKE too, but only
  // for Marble sponsorships started on/after the cutover date).
  const intakeCountsTowardCap = isIntakeCapGatedForCareSponsorship(
    careSponsorship.CareSponsor.careSponsorType,
    careSponsorship.startedAt,
  );
  const capGatedAppointmentTypes = getCareSponsorshipCapGatedAppointmentTypes(
    careSponsorship.CareSponsor.careSponsorType,
    careSponsorship.startedAt,
  );

  const userAppointments = await prisma.userAppointment.findMany({
    where: {
      careSponsorshipId: careSponsorship.id,
      isBilledClient: true,
      Appointment: {
        appointmentType: { in: capGatedAppointmentTypes as AppointmentType[] },
        ...(careSponsorship.endedAt != null && {
          startTime: { lte: careSponsorship.endedAt },
        }),
      },
    },
    include: {
      Appointment: {
        include: { CancelledByUser: true },
      },
    },
    orderBy: { Appointment: { startTime: "asc" } },
  });

  let unexcusedNoShowLikeCount = 0;
  let completedSessions = 0;
  let scheduledSessions = 0;
  let ordinal = 0;
  const appointments: CareSponsorshipSessionAppointment[] = [];

  for (const userAppointment of userAppointments) {
    const appointment = userAppointment.Appointment;

    const wasCancelledByTherapist =
      appointment.cancelledAt != null &&
      appointment.CancelledByUser?.userType === UserType.THERAPIST;
    if (wasCancelledByTherapist) {
      // Never the family's/sponsor's issue — excluded entirely, no ordinal.
      continue;
    }

    // "Late" is relative to when it was cancelled vs. when it was due to
    // start — not the current wall-clock time. (isNHoursOrLessFromNow is the
    // wrong tool here: it compares a date to right now, which would make
    // every past appointment "late" once enough real time has passed.)
    const wasCancelled = appointment.cancelledAt != null;
    const wasLateCancellation =
      wasCancelled &&
      appointment.startTime.getTime() - appointment.cancelledAt!.getTime() <
        LATE_CANCELLATION_WINDOW_HOURS * ONE_HOUR_MS;
    if (wasCancelled && !wasLateCancellation) {
      // Cancelled with >=24h notice — doesn't count at all.
      continue;
    }

    const wasExcusedNoShow =
      userAppointment.noShowed && userAppointment.noShowExcusedReason != null;
    if (wasExcusedNoShow) {
      // Mirrors the 30-day care-gap system's treatment — never counts,
      // regardless of the allotment-exemption rule.
      continue;
    }

    const wasUnexcusedNoShowLike =
      (userAppointment.noShowed && !userAppointment.noShowExcusedReason) ||
      wasLateCancellation;
    const wasCompleted = appointment.completed && !wasCancelled;

    if (!wasCompleted && !wasUnexcusedNoShowLike) {
      // Not yet occurred (or not yet marked either way) — still on the books.
      ordinal += 1;
      scheduledSessions += 1;
      appointments.push({
        appointmentId: appointment.id,
        userAppointmentId: userAppointment.id,
        ordinal,
        startTime: appointment.startTime,
        countsTowardCap: true,
        billableToSponsor: true,
        status: "scheduled",
      });
      continue;
    }

    let isAllotmentExemptUnexcusedNoShow = false;
    if (wasUnexcusedNoShowLike) {
      unexcusedNoShowLikeCount += 1;
      // Billed at the normal rate but excluded from the cap / allotment.
      isAllotmentExemptUnexcusedNoShow =
        unexcusedNoShowLikeCount <= ALLOTMENT_EXEMPT_UNEXCUSED_NO_SHOW_COUNT;
    }

    if (isAllotmentExemptUnexcusedNoShow) {
      appointments.push({
        appointmentId: appointment.id,
        userAppointmentId: userAppointment.id,
        startTime: appointment.startTime,
        countsTowardCap: false,
        billableToSponsor: true,
        status: "completed",
      });
      continue;
    }

    ordinal += 1;
    completedSessions += 1;
    appointments.push({
      appointmentId: appointment.id,
      userAppointmentId: userAppointment.id,
      ordinal,
      startTime: appointment.startTime,
      countsTowardCap: true,
      billableToSponsor: true,
      status: "completed",
    });
  }

  return {
    completedSessions,
    scheduledSessions,
    usedSessions: completedSessions + scheduledSessions,
    cap,
    appointments,
    careSponsorType: careSponsorship.CareSponsor.careSponsorType,
    sponsorshipStartedAt: careSponsorship.startedAt,
    intakeCountsTowardCap,
  };
};

/**
 * Ended (archived) CareSponsorships for a student, newest end first, with
 * per-sponsorship session usage. Used by the Insurance Details history dialog.
 */
export const getEndedCareSponsorshipsForStudent = async (studentId: number) => {
  const endedSponsorships = await prisma.careSponsorship.findMany({
    where: {
      studentId,
      endedAt: { not: null },
    },
    include: { CareSponsor: true },
    orderBy: { endedAt: "desc" },
  });

  return Promise.all(
    endedSponsorships.map(async (sponsorship) => {
      const usage = await getCareSponsorshipSessionUsage({
        studentId,
        careSponsorshipId: sponsorship.id,
      });

      return {
        id: sponsorship.id,
        startedAt: sponsorship.startedAt,
        endedAt: sponsorship.endedAt!,
        offboardedReason: sponsorship.offboardedReason,
        careSponsorType: sponsorship.CareSponsor.careSponsorType,
        sponsorName: sponsorship.CareSponsor.name,
        usedSessions: usage.usedSessions,
        cap: usage.cap,
      };
    }),
  );
};

/**
 * Students with an active CareSponsorship whose next scheduled appointment
 * is the last one covered by their session cap (`ordinal === cap`) and
 * falls within `[windowStart, windowEnd)`. The optional catch-up window also
 * includes nearer appointments changed since the previous cron run, so a
 * late booking or reschedule does not miss the heads-up.
 *
 * `careSponsorTypes` narrows the scan to those sponsor types. Session usage
 * is computed per sponsorship, so a caller that only notifies some sponsor
 * types should pass them rather than filter the results — otherwise the work
 * is spent on sponsorships that were never going to produce a notification.
 */
export const getUpcomingSponsoredSessionEndings = async ({
  windowStart,
  windowEnd,
  catchUp,
  careSponsorTypes,
}: {
  windowStart: Date;
  windowEnd: Date;
  catchUp?: {
    upcomingStart: Date;
    changedSince: Date;
  };
  careSponsorTypes?: readonly CareSponsorType[];
}): Promise<UpcomingSponsoredSessionEnding[]> => {
  const studentsWithActiveSponsorship = await prisma.student.findMany({
    where: {
      activeCareSponsorshipId: { not: null },
      ...(careSponsorTypes != null && {
        ActiveCareSponsorship: {
          CareSponsor: {
            careSponsorType: { in: careSponsorTypes as CareSponsorType[] },
          },
        },
      }),
    },
    select: {
      id: true,
      ActiveCareSponsorship: {
        select: {
          id: true,
          endingSoonNotifiedAt: true,
          CareSponsor: { select: { name: true, careSponsorType: true } },
        },
      },
    },
  });

  const endings = await Promise.all(
    studentsWithActiveSponsorship.map(
      async (student): Promise<UpcomingSponsoredSessionEnding | null> => {
        const sponsorship = student.ActiveCareSponsorship;
        if (!sponsorship) {
          return null;
        }

        const usage = await getCareSponsorshipSessionUsage({
          studentId: student.id,
        });
        if (usage.cap == null) {
          return null;
        }

        const lastSponsoredAppointment = usage.appointments.find(
          (a) => a.ordinal === usage.cap && a.status === "scheduled",
        );
        if (!lastSponsoredAppointment) {
          return null;
        }

        const isInRegularWindow =
          lastSponsoredAppointment.startTime >= windowStart &&
          lastSponsoredAppointment.startTime < windowEnd;
        let isInCatchUpWindow = false;
        if (
          catchUp &&
          lastSponsoredAppointment.startTime >= catchUp.upcomingStart &&
          lastSponsoredAppointment.startTime < windowStart
        ) {
          const appointment = await prisma.appointment.findUnique({
            where: { id: lastSponsoredAppointment.appointmentId },
            select: { updatedAt: true },
          });
          isInCatchUpWindow =
            appointment != null &&
            sponsorship.endingSoonNotifiedAt == null &&
            appointment.updatedAt >= catchUp.changedSince;
        }

        if (!isInRegularWindow && !isInCatchUpWindow) {
          return null;
        }

        return {
          studentId: student.id,
          careSponsorshipId: sponsorship.id,
          sponsorName: sponsorship.CareSponsor.name,
          careSponsorType: sponsorship.CareSponsor.careSponsorType,
          sessionCap: usage.cap,
          appointmentId: lastSponsoredAppointment.appointmentId,
          appointmentStartTime: lastSponsoredAppointment.startTime,
        };
      },
    ),
  );

  return endings.filter((ending) => ending != null);
};

/**
 * Students with an active CareSponsorship whose last capped session
 * (`ordinal === cap`) started before `before` — i.e. the calendar day after
 * that session has begun. Used by the daily CAP_REACHED auto-end cron.
 *
 * Includes both completed and still-scheduled last sessions: scheduled ones
 * already consume cap headroom, and once their start day has passed the
 * sponsorship should end even if the therapist has not finalized yet.
 */
export const getSponsoredSessionCapReachedEndings = async ({
  before,
}: {
  before: Date;
}): Promise<SponsoredSessionCapReachedEnding[]> => {
  const studentsWithActiveSponsorship = await prisma.student.findMany({
    where: { activeCareSponsorshipId: { not: null } },
    select: {
      id: true,
      ActiveCareSponsorship: {
        select: { id: true },
      },
    },
  });

  const endings = await Promise.all(
    studentsWithActiveSponsorship.map(
      async (student): Promise<SponsoredSessionCapReachedEnding | null> => {
        const sponsorship = student.ActiveCareSponsorship;
        if (!sponsorship) {
          return null;
        }

        const usage = await getCareSponsorshipSessionUsage({
          studentId: student.id,
        });
        if (usage.cap == null || usage.usedSessions < usage.cap) {
          return null;
        }

        const lastSponsoredAppointment = usage.appointments.find(
          (a) => a.ordinal === usage.cap,
        );
        if (!lastSponsoredAppointment) {
          return null;
        }

        if (lastSponsoredAppointment.startTime >= before) {
          return null;
        }

        return {
          studentId: student.id,
          careSponsorshipId: sponsorship.id,
          sessionCap: usage.cap,
          appointmentId: lastSponsoredAppointment.appointmentId,
          appointmentStartTime: lastSponsoredAppointment.startTime,
        };
      },
    ),
  );

  return endings.filter((ending) => ending != null);
};

/**
 * How many GUARDIAN_CONSULT appointments have been attributed to this
 * CareSponsorship, for enforcing {@link GUARDIAN_CONSULT_SPONSORSHIP_CAP}.
 * Mirrors {@link getCareSponsorshipSessionUsage}'s exclusion rules
 * (therapist-initiated cancellations, >=24h client cancellations, and
 * excused no-shows never count) but needs no ordinals or billing
 * distinction — a guardian consult is always fully sponsor-covered, so this
 * only needs a plain count against the cap.
 */
const getGuardianConsultUsageCountForSponsorship = async (
  careSponsorshipId: number,
): Promise<number> => {
  const userAppointments = await prisma.userAppointment.findMany({
    where: {
      careSponsorshipId,
      isBilledClient: true,
      Appointment: { appointmentType: AppointmentType.GUARDIAN_CONSULT },
    },
    include: {
      Appointment: { include: { CancelledByUser: true } },
    },
  });

  let count = 0;
  for (const userAppointment of userAppointments) {
    const appointment = userAppointment.Appointment;

    const wasCancelledByTherapist =
      appointment.cancelledAt != null &&
      appointment.CancelledByUser?.userType === UserType.THERAPIST;
    if (wasCancelledByTherapist) {
      continue;
    }

    const wasCancelled = appointment.cancelledAt != null;
    const wasLateCancellation =
      wasCancelled &&
      appointment.startTime.getTime() - appointment.cancelledAt!.getTime() <
        LATE_CANCELLATION_WINDOW_HOURS * ONE_HOUR_MS;
    if (wasCancelled && !wasLateCancellation) {
      // Cancelled with >=24h notice — doesn't consume the family's one shot.
      continue;
    }

    const wasExcusedNoShow =
      userAppointment.noShowed && userAppointment.noShowExcusedReason != null;
    if (wasExcusedNoShow) {
      continue;
    }

    count += 1;
  }

  return count;
};

/**
 * Booking-time gate with distinct block reasons for a student with an active
 * CareSponsorship:
 *  - `NOT_ELIGIBLE_TYPE` when `appointmentType` isn't covered by this
 *    sponsorship's `CareSponsorType` (see
 *    `CARE_SPONSORSHIP_ELIGIBLE_APPOINTMENT_TYPES_BY_SPONSOR_TYPE`) — blocks
 *    scheduling even if the therapist would otherwise be INN via leftover
 *    insurance, matching the bookable-types filter.
 *  - `GUARDIAN_CONSULT_CAP_REACHED` for the separate one-consult cap.
 *  - `SPONSORSHIP_CAP_REACHED` for types that consume the numeric allotment
 *    for this sponsor type (see
 *    `getCareSponsorshipCapGatedAppointmentTypes`) — for INTAKE
 *    specifically, this also depends on whether the sponsorship started
 *    on/after `CARE_SPONSORSHIP_INTAKE_CAP_GATING_CUTOVER_DATE`, though in
 *    practice a student can only ever have one intake, so this rarely bites.
 *  - Eligible but not cap-gated types (e.g. INTAKE for SCHOOL/SCHOOL_NETWORK,
 *    or for a Marble sponsorship that predates the INTAKE cutover) return
 *    `{ canBook: true }` even when the therapy-session cap is exhausted.
 *
 * When `options.appointmentType` is omitted, this checks cap headroom alone
 * (no type-eligibility or type-specific gate).
 *
 * Info release is now optional for care sponsorship (made optional in
 * onboarding as of Jul 2026).
 */
export const getCanBookAdditionalSponsoredSession = async (
  studentId: number,
  options?: { appointmentType?: AppointmentType },
): Promise<CanBookAdditionalSponsoredSessionResult> => {
  const appointmentType = options?.appointmentType;

  if (appointmentType === AppointmentType.GUARDIAN_CONSULT) {
    const activeCareSponsorship =
      await getActiveCareSponsorshipForStudent(studentId);
    if (!activeCareSponsorship) {
      return { canBook: true };
    }
    const guardianConsultsUsed =
      await getGuardianConsultUsageCountForSponsorship(
        activeCareSponsorship.id,
      );
    if (guardianConsultsUsed < GUARDIAN_CONSULT_SPONSORSHIP_CAP) {
      return { canBook: true };
    }
    return { canBook: false, reason: "GUARDIAN_CONSULT_CAP_REACHED" };
  }

  // Reuse this same lookup's `careSponsorType` for the type-specific gates
  // below instead of a second, separate resolution.
  const usage = await getCareSponsorshipSessionUsage({ studentId });

  if (appointmentType != null && usage.careSponsorType != null) {
    if (
      !isCareSponsorshipEligibleAppointmentTypeForSponsorType(
        appointmentType,
        usage.careSponsorType,
      )
    ) {
      return { canBook: false, reason: "NOT_ELIGIBLE_TYPE" };
    }

    // Passing usage.sponsorshipStartedAt makes this correct for INTAKE too
    // (cap-gated only for a Marble sponsorship started on/after the cutover
    // — see getCareSponsorshipCapGatedAppointmentTypes) without special-
    // casing it here.
    if (
      !isCareSponsorshipCapGatedAppointmentType(
        appointmentType,
        usage.careSponsorType,
        usage.sponsorshipStartedAt,
      )
    ) {
      return { canBook: true };
    }
  }

  if (usage.cap == null || usage.usedSessions < usage.cap) {
    return { canBook: true };
  }
  return { canBook: false, reason: "SPONSORSHIP_CAP_REACHED" };
};

/**
 * Boolean wrapper around {@link getCanBookAdditionalSponsoredSession} for
 * callers that only need a yes/no gate (booking attribution, etc.).
 */
export const canBookAdditionalSponsoredSession = async (
  studentId: number,
  options?: { appointmentType?: AppointmentType },
): Promise<boolean> => {
  const result = await getCanBookAdditionalSponsoredSession(studentId, options);
  return result.canBook;
};

/**
 * Stamp careSponsorshipId on the billed-client UserAppointment at booking
 * time when the student has an active sponsorship, the appointment type is
 * covered, and there is remaining session-cap headroom.
 *
 * When an active sponsorship exists but attribution is refused, returns
 * `unattributedCareSponsorshipId` so callers can alert Care Ops — the
 * appointment is still created, just not attributed to the sponsor.
 */
export const resolveCareSponsorshipIdForBooking = async ({
  studentId,
  appointmentType,
}: {
  studentId: number | undefined;
  appointmentType: AppointmentType;
}): Promise<ResolveCareSponsorshipIdForBookingResult> => {
  if (!studentId) {
    return {
      careSponsorshipId: null,
      unattributedCareSponsorshipId: null,
      skipReason: "NOT_ELIGIBLE_TYPE",
    };
  }

  const activeCareSponsorship =
    await getActiveCareSponsorshipForStudent(studentId);
  if (!activeCareSponsorship) {
    return {
      careSponsorshipId: null,
      unattributedCareSponsorshipId: null,
      skipReason: "NO_ACTIVE_SPONSORSHIP",
    };
  }

  // What THIS sponsorship's CareSponsorType actually covers (e.g.
  // MEDICATION_MANAGEMENT/FAMILY_THERAPY are only sponsor-covered for
  // INSURANCE_PAYER_NETWORK_STATUS, not SCHOOL/SCHOOL_NETWORK).
  if (
    !isCareSponsorshipEligibleAppointmentTypeForSponsorType(
      appointmentType,
      activeCareSponsorship.CareSponsor.careSponsorType,
    )
  ) {
    return {
      careSponsorshipId: null,
      unattributedCareSponsorshipId: null,
      skipReason: "NOT_ELIGIBLE_TYPE",
    };
  }

  const canBookAdditionalSession = await getCanBookAdditionalSponsoredSession(
    studentId,
    { appointmentType },
  );
  if (canBookAdditionalSession.canBook) {
    return {
      careSponsorshipId: activeCareSponsorship.id,
      unattributedCareSponsorshipId: null,
    };
  }

  return {
    careSponsorshipId: null,
    unattributedCareSponsorshipId: activeCareSponsorship.id,
    skipReason: canBookAdditionalSession.reason,
  };
};
